import React, { useCallback, useMemo } from 'react';
import type { ProjectType } from '@jira/platform__project-types/src/project-types.tsx';
import { token } from '@atlaskit/tokens';
import Tooltip from '@atlaskit/tooltip';
import Avatar from '@atlaskit/avatar';
import Heading from '@atlaskit/heading';
import PersonIcon from '@atlaskit/icon/core/person';
import PeopleGroupIcon from '@atlaskit/icon/core/people-group';
import { Anchor, Inline, Stack, Text } from '@atlaskit/primitives/compiled';
import { cssMap } from '@atlaskit/css';
import { defineMessages, useIntl, type MessageDescriptor } from '@atlassian/jira-intl';
import { fireUIAnalytics, useAnalyticsEvents } from '@atlassian/jira-product-analytics-bridge';
import type { ProjectRecommendation } from './types.tsx';

const messages = defineMessages({
	usedByTeammate: {
		id: 'home-project-recommendations.used-by-teammate.non-final',
		defaultMessage: 'Used by {teammate}',
		description:
			'Recommendation reason displayed on project card when a specific teammate uses the project',
	},
	popularWithTeammates: {
		id: 'home-project-recommendations.popular-with-teammates.non-final',
		defaultMessage: 'Popular with teammates',
		description:
			'Recommendation reason displayed on project card when the project is popular among teammates',
	},
	recommendationCardAriaLabel: {
		id: 'home-project-recommendations.recommendation-card-aria-label.non-final',
		defaultMessage:
			'Recommended project: {projectName}, a {projectType} project. Recommendation reason: {recommendationType}',
		description: 'Accessible label for screen readers describing the recommended project card',
	},
	businessProject: {
		id: 'home-project-recommendations.business-project.non-final',
		defaultMessage: 'Business project',
		description: 'Label displayed on project card indicating a business project type',
	},
	customerServiceProject: {
		id: 'home-project-recommendations.customer-service-project.non-final',
		defaultMessage: 'Customer service project',
		description: 'Label displayed on project card indicating a customer service project type',
	},
	productDiscoveryProject: {
		id: 'home-project-recommendations.product-discovery-project.non-final',
		defaultMessage: 'Product discovery',
		description: 'Label displayed on project card indicating a product discovery project type',
	},
	serviceDeskProject: {
		id: 'home-project-recommendations.service-desk-project.non-final',
		defaultMessage: 'Service desk project',
		description: 'Label displayed on project card indicating a service desk project type',
	},
	softwareProject: {
		id: 'home-project-recommendations.software-project.non-final',
		defaultMessage: 'Software project',
		description: 'Label displayed on project card indicating a software project type',
	},
});

const styles = cssMap({
	cardStyles: {
		width: '259px',
		minWidth: '259px',
		height: '144px',
		textDecoration: 'none',
		display: 'block',
		backgroundColor: token('elevation.surface'),
		paddingTop: token('space.200'),
		paddingRight: token('space.200'),
		paddingBottom: token('space.200'),
		paddingLeft: token('space.200'),
		borderColor: token('color.border'),
		borderStyle: 'solid',
		borderWidth: token('border.width'),
		borderRadius: token('radius.small'),
		color: token('color.text'),
		'&:hover': {
			backgroundColor: token('elevation.surface.hovered'),
			textDecoration: 'none',
			color: token('color.text'),
		},
		'&:active': {
			backgroundColor: token('elevation.surface.pressed'),
			textDecoration: 'none',
		},
		'&:focus': {
			textDecoration: 'none',
		},
		'&:visited': {
			textDecoration: 'none',
		},
	},
});

const getProjectUrl = (projectKey: string): string => `/browse/${projectKey}`;

export type RecommendedProjectCardProps = {
	recommendation: ProjectRecommendation;
};

const RecommendedProjectCard = ({
	recommendation,
}: RecommendedProjectCardProps): React.ReactElement => {
	const { project, details } = recommendation;
	const { name, avatarUrl, key, type: projectType } = project;
	const { formatMessage } = useIntl();
	const { createAnalyticsEvent } = useAnalyticsEvents();

	const recommendationText =
		details.type === 'TENANT_ACTIVITY'
			? formatMessage(messages.popularWithTeammates)
			: formatMessage(messages.usedByTeammate, {
					teammate: details.user.name,
				});

	const recommendationIcon = useMemo(() => {
		return details.type === 'TENANT_ACTIVITY' ? (
			<PeopleGroupIcon label="" size="small" />
		) : (
			<PersonIcon label="" size="small" />
		);
	}, [details.type]);

	const onCardClick = useCallback(() => {
		const analyticsEvent = createAnalyticsEvent({
			action: 'clicked',
			actionSubject: 'projectCard',
		});
		fireUIAnalytics(analyticsEvent, 'recommendedProjectCard', {
			projectType,
			recommendationType: details.type,
			recommendationRelevancyScore: details.relevancyScore,
		});
	}, [createAnalyticsEvent, projectType, details.type, details.relevancyScore]);

	return (
		<Anchor
			href={getProjectUrl(key)}
			xcss={styles.cardStyles}
			onClick={onCardClick}
			aria-label={formatMessage(messages.recommendationCardAriaLabel, {
				projectName: name,
				projectType,
				recommendationType: recommendationText,
			})}
		>
			<Tooltip content={name}>
				<Stack>
					<Avatar appearance="square" size="medium" src={avatarUrl} />
					<Stack space="space.300">
						<Stack>
							<Heading as="h4" size="xsmall">
								<Text maxLines={1} weight="semibold" color="color.text">
									{name}
								</Text>
							</Heading>
							<Text weight="regular" size="small" as="p" maxLines={1} color="color.text.subtle">
								{formatMessage(getProjectTypeMessage(projectType))}
							</Text>
						</Stack>
						<Inline space="space.025" alignBlock="center">
							{recommendationIcon}
							<Text weight="regular" size="small" as="p" maxLines={1} color="color.text.subtle">
								{recommendationText}
							</Text>
						</Inline>
					</Stack>
				</Stack>
			</Tooltip>
		</Anchor>
	);
};

const getProjectTypeMessage = (projectType: ProjectType): MessageDescriptor => {
	switch (projectType) {
		case 'business':
			return messages.businessProject;
		case 'customer_service':
			return messages.customerServiceProject;
		case 'product_discovery':
			return messages.productDiscoveryProject;
		case 'service_desk':
			return messages.serviceDeskProject;
		case 'software':
		default:
			return messages.softwareProject;
	}
};

export default RecommendedProjectCard;
